import React from 'react'
import { diffSettings, type FieldChange, type FlattenOptions } from '../utils/settingsView'
import type {
  AppObjectRole,
  DeploymentPreflightItem,
  IntuneAutomatedUpdatesSnapshot,
  OverrideChange,
  PerAppObject,
} from '../services/api'

/**
 * "Changes to the existing deployment": what an in-place update changes against Intune now, per
 * app object, from the preflight's `changes` (computed by the same function the worker runs).
 * Rendered on the deploy wizard's Overview and in the in-place confirmation.
 */

const ROLE_LABEL: Record<AppObjectRole, string> = {
  install: 'install app',
  update_only: 'update-only app',
}

const SECTION_LABEL: Record<OverrideChange['section'], string> = {
  experience: 'user experience',
  app_information: 'app information',
  advanced: 'advanced settings',
  assignments: 'assignments',
  automated_updates: 'update wait and versions kept',
}

/** Graph's Company Portal and requirement fields, named as the Intune admin center names them. */
const FIELD_LABELS: Record<string, string> = {
  owner: 'Owner',
  developer: 'Developer',
  notes: 'Notes',
  informationUrl: 'Information URL',
  privacyUrl: 'Privacy URL',
  isFeatured: 'Featured in Company Portal',
  featured: 'Featured in Company Portal',
  allowAvailableUninstall: 'Uninstall in Company Portal',
  categories: 'Categories',
  minimumSupportedWindowsRelease: 'Minimum Windows release',
  minimumOs: 'Minimum Windows release',
  minimumFreeDiskSpaceInMB: 'Minimum disk space (MB)',
  minimumFreeDiskSpaceMB: 'Minimum disk space (MB)',
  minimumMemoryInMB: 'Minimum memory (MB)',
  minimumMemoryMB: 'Minimum memory (MB)',
  minimumNumberOfProcessors: 'Minimum processors',
  minimumProcessors: 'Minimum processors',
  minimumCpuSpeedInMHz: 'Minimum CPU speed (MHz)',
  minimumCpuSpeedMHz: 'Minimum CPU speed (MHz)',
  allowedArchitectures: 'Architectures',
  architectures: 'Architectures',
  returnCodes: 'Return codes',
  requirementRules: 'Requirement rules',
  'installExperience.deviceRestartBehavior': 'Device restart behaviour',
  'installExperience.runAsAccount': 'Install context',
}

const ROLES: AppObjectRole[] = ['install', 'update_only']

/** The entries of a per-app-object map, install app first. */
const byRole = <T,>(v: PerAppObject<T> | null | undefined): (T & { role: AppObjectRole })[] =>
  ROLES.filter((r) => v?.[r]).map((r) => ({ ...(v![r] as T), role: r }))

/** The renames an in-place update makes. Without `changes`, the item's own name pair says it for
 *  the app object that stands for the deployment. */
export function renameTexts(i: DeploymentPreflightItem): string[] {
  if (i.mode !== 'update_in_place') return []
  const pairs = i.changes?.rename
    ? byRole(i.changes.rename)
    : i.display_name && i.rendered_name
      ? [{ role: i.role ?? 'install', from: i.display_name, to: i.rendered_name }]
      : []
  return pairs
    .filter((r) => r.from && r.to && r.from !== r.to)
    .map((r) => `The ${ROLE_LABEL[r.role] ?? 'app'} will be renamed from ${r.from} to ${r.to}.`)
}

function updateWait(au: IntuneAutomatedUpdatesSnapshot | null | undefined): string {
  if (!au) return 'Not set'
  if (!au.followNewVersions) return 'Manual'
  const days = au.soakDays ?? 0
  return days === 0 ? 'Published immediately' : `Published ${days} day${days === 1 ? '' : 's'} after release`
}

/** Each kept or replaced edit as "<section> (<app object>)", e.g. "assignments (install app)". */
const sectionList = (edits: OverrideChange[]) =>
  edits
    .map((e) => `${SECTION_LABEL[e.section] ?? e.section} (${ROLE_LABEL[e.role] ?? e.role})`)
    .join(', ')

function Rows({ title, changes }: { title: string; changes: FieldChange[] }) {
  if (!changes.length) return null
  return (
    <div className="mt-2">
      <p className="mb-1 text-[12px] font-medium text-theme-text-secondary">{title}</p>
      <dl className="space-y-1">
        {changes.map((c) => (
          <div key={c.path} className="flex items-start justify-between gap-4 text-[12px]">
            <dt className="text-theme-text-tertiary">{c.label}</dt>
            <dd className="flex min-w-0 items-baseline gap-1.5 text-right">
              <span className="text-theme-text-tertiary">{c.from}</span>
              <span className="text-theme-text-tertiary">&rarr;</span>
              <span className="break-words font-medium text-theme-text-primary">{c.to}</span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default function DeploymentChanges({
  item,
  experienceView,
  deliveryView,
}: {
  item: DeploymentPreflightItem
  /** Describers for the user experience configuration. */
  experienceView?: FlattenOptions
  /** Describers for the deployment template's fields, used with the Graph field names. */
  deliveryView?: FlattenOptions
}) {
  // A new version is a new app: the only change to show is which page edits it keeps and drops.
  const newVersion = item.mode !== 'update_in_place'
  if (
    newVersion &&
    !item.changes?.overrides?.kept?.length &&
    !item.changes?.overrides?.dropped?.length
  ) {
    return null
  }
  const c = item.changes ?? null
  const renames = renameTexts(item)

  const settingsDiffs = (
    section: 'experience' | 'app_information' | 'advanced',
    sectionTitle: string,
    view?: FlattenOptions,
  ) =>
    byRole(c?.[section]).map((s) => ({
      key: `${section}-${s.role}`,
      title: `${sectionTitle} (${ROLE_LABEL[s.role] ?? s.role})`,
      changes: diffSettings(s.from ?? {}, s.to ?? {}, {
        ...view,
        labels: { ...FIELD_LABELS, ...(view?.labels ?? {}) },
        includeRemoved: true,
      }),
    }))

  const diffs = [
    ...settingsDiffs('experience', 'User experience', experienceView),
    ...settingsDiffs('app_information', 'App information', deliveryView),
    ...settingsDiffs('advanced', 'Advanced settings', deliveryView),
  ].filter((d) => d.changes.length)

  const assignments = byRole(c?.assignments).filter((a) => a.added?.length || a.removed?.length)
  const au = c?.automated_updates ?? null
  const kept = c?.overrides?.kept ?? []
  const dropped = c?.overrides?.dropped ?? []

  const warnings = c?.warnings ?? []
  const nothing =
    !!c && !renames.length && !diffs.length && !assignments.length && !kept.length &&
    !dropped.length && !warnings.length && updateWait(au?.from) === updateWait(au?.to)

  if (!c && !renames.length && !item.changes_skipped && !item.changes_error) return null

  return (
    <div className="mt-2 rounded-lg border border-theme-border-primary bg-theme-tertiary px-3 py-2 text-[12px] text-theme-text-secondary">
      <p className="label">Changes to the existing deployment</p>

      {renames.map((r) => (
        <p key={r} className="mt-1 text-amber-500">{r}</p>
      ))}

      {diffs.map((d) => (
        <Rows key={d.key} title={d.title} changes={d.changes} />
      ))}

      {assignments.map((a) => (
        <div key={a.role} className="mt-2">
          <p className="mb-1 font-medium">Assignments ({ROLE_LABEL[a.role] ?? a.role})</p>
          {!!a.added?.length && <p>Added: {a.added.join(', ')}</p>}
          {!!a.removed?.length && <p className="text-amber-500">Removed: {a.removed.join(', ')}</p>}
        </div>
      ))}

      {au && (
        <p className="mt-2">
          New versions:{' '}
          {updateWait(au.from) === updateWait(au.to) ? (
            <span className="font-medium text-theme-text-primary">{updateWait(au.to)}, unchanged</span>
          ) : (
            <>
              <span className="text-theme-text-tertiary">{updateWait(au.from)}</span> &rarr;{' '}
              <span className="font-medium text-theme-text-primary">{updateWait(au.to)}</span>
            </>
          )}
        </p>
      )}

      {kept.length > 0 && (
        <p className="mt-2">
          Kept from the deployment page: {sectionList(kept)}.{' '}
          {newVersion
            ? 'The new version keeps these edits.'
            : 'The template is unchanged, so these edits stay.'}
        </p>
      )}
      {dropped.length > 0 && (
        <p className="mt-2 text-amber-500">
          Replaced by the template: {sectionList(dropped)}. The edits made on the deployment page
          are lost.
        </p>
      )}
      {newVersion && c?.new_version_drops_edits && (
        <p className="mt-2 text-amber-500">
          The new version is published with the templates&rsquo; values for these sections: the
          edits made on the deployment page are lost.
        </p>
      )}
      {warnings.map((w) => (
        <p key={w} className="mt-2 text-amber-500">{w}</p>
      ))}
      {nothing && <p className="mt-1">No settings change.</p>}
      {!c && item.changes_error && (
        <p className="mt-1 text-amber-500">
          The changes to the existing deployment could not be listed: {item.changes_error}
        </p>
      )}
      {item.changes_skipped === 'too_many' && (
        <p className="mt-1">
          The changes are not listed when more than 25 apps are deployed at once. Deploy fewer apps
          together to see them.
        </p>
      )}
    </div>
  )
}
